@if (loading()) {
  <div class="flex items-center justify-center py-12">
    <p class="text-neutral-text text-[14px]" i18n="@@adminEditCategoryLoading">Loading category...</p>
  </div>
} @else if (errorLoadingCategory()) {
  <div class="rounded-[6px] border border-red-200 bg-red-50 px-4 py-3">
    <p class="text-[14px] text-red-800">{{ errorLoadingCategory() }}</p>
  </div>
} @else if (category) {
  <div class="flex flex-col gap-4">
    <div class="relative rounded-2xl bg-white p-6 shadow-sm">
      <div class="absolute top-4 right-4">
        <div class="relative inline-flex items-center justify-end">
          <button
            (click)="toggleCategoryDropdown($event)"
            class="hover-bg-border flex h-[28px] w-[28px] items-center justify-center rounded-[6px] text-gray-600 hover:cursor-pointer hover:text-gray-900">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round">
              <circle cx="12" cy="12" r="1"></circle>
              <circle cx="12" cy="5" r="1"></circle>
              <circle cx="12" cy="19" r="1"></circle>
            </svg>
          </button>
          @if (showCategoryDropdown()) {
            <div
              class="absolute top-full right-0 z-50 mt-1 flex w-48 flex-col gap-1 rounded-md border border-gray-200 bg-white p-1 shadow-lg">
              <button
                (click)="handleCategoryAction('Edit Category')"
                class="w-full rounded-[4px] px-3 py-2 text-left text-[13px] text-gray-700 hover:cursor-pointer hover:bg-gray-50">
                <span i18n="@@adminCategoriesActionEdit">Edit Category</span>
              </button>
              <button
                (click)="handleCategoryAction('Delete Category')"
                class="w-full rounded-[4px] px-3 py-2 text-left text-[13px] text-gray-700 hover:cursor-pointer hover:bg-gray-50">
                <span i18n="@@adminCategoriesActionDelete">Delete Category</span>
              </button>
            </div>
          }
        </div>
      </div>

      <div class="flex items-start gap-4">
        <div class="flex h-[120px] w-[120px] items-center justify-center overflow-hidden rounded-lg bg-gray-50">
          <img [src]="imagePreviewUrl()" [alt]="category.titleEn" class="h-full w-full object-cover" />
        </div>
        <div class="flex-1">
          <h2 class="text-black-soft mb-2 text-[24px] font-[600]">
            {{ isArabic() ? category.titleAr : category.titleEn }}
          </h2>
          @if (category.descriptionEn) {
            <p class="text-neutral-text text-[14px] leading-relaxed">
              {{ isArabic() ? category.descriptionAr : category.descriptionEn }}
            </p>
          }
        </div>
      </div>
    </div>

    <div class="flex flex-col gap-2 rounded-2xl bg-white p-6 shadow-sm">
      <div class="flex items-center justify-between">
        <h3 class="text-black-soft text-[20px] font-[600]" i18n="@@adminViewCategorySubCategoriesTitle">
          Sub Categories
        </h3>
        <button
          (click)="onAddSubCategory()"
          class="bg-primary hover-bg-primary-hover flex items-center gap-1 rounded-[8px] px-[10px] py-[5px] text-[13px] font-[500] text-[white] hover:cursor-pointer">
          <svg xmlns="http://www.w3.org/2000/svg" width="20px" height="20px" viewBox="0 0 24 24">
            <path
              fill="currentColor"
              d="M18 12.998h-5v5a1 1 0 0 1-2 0v-5H6a1 1 0 0 1 0-2h5v-5a1 1 0 0 1 2 0v5h5a1 1 0 0 1 0 2" />
          </svg>
          <span i18n="@@adminViewCategoryAddSubCategoryButton">Add Sub Category</span>
        </button>
      </div>

      @if (loadingSubCategories()) {
        <div class="flex items-center justify-center py-12">
          <p class="text-neutral-text text-[14px]" i18n="@@adminViewCategoryLoadingSubCategories">
            Loading sub categories...
          </p>
        </div>
      } @else {
        <div class="border-border mt-2 overflow-visible rounded-[8px] border bg-white shadow-sm">
          <table class="w-full text-left text-sm text-gray-500">
            <thead class="bg-selected text-text-gray text-xs font-[600] uppercase">
              <tr>
                <th class="w-px py-3 ps-4 pe-6 text-center whitespace-nowrap">#</th>
                <th class="px-6 py-3 text-center" i18n="@@adminViewCategorySubCategoryNameHeader">
                  Sub Category Name
                </th>
                <th class="px-6 py-3 text-center" i18n="@@adminCategoriesTableHeaderDateCreated">
                  Date Created
                </th>
                <th class="px-6 py-3 text-center" i18n="@@adminCategoriesTableHeaderLastUpdated">
                  Last Updated
                </th>
                <th class="w-px py-3 ps-6 pe-4 text-center"></th>
              </tr>
            </thead>
            <tbody>
              @if (paginatedSubCategories.length === 0) {
                <tr class="border-border border-b bg-white">
                  <td
                    colspan="5"
                    class="text-neutral-text px-6 py-4 text-center text-[14px]"
                    i18n="@@adminViewCategoryNoSubCategories">
                    No sub categories found.
                  </td>
                </tr>
              } @else {
                @for (subCategory of paginatedSubCategories; track subCategory.id; let i = $index) {
                  <tr class="border-border border-b bg-white hover:bg-gray-50">
                    <td class="py-4 ps-4 pe-6 text-center font-medium text-gray-900">
                      {{ i + 1 + (currentPage - 1) * itemsPerPage }}
                    </td>
                    <td class="px-6 py-4 text-center font-medium text-gray-900">
                      {{ isArabic() ? subCategory.titleAr : subCategory.titleEn }}
                    </td>
                    <td class="px-6 py-4 text-center text-gray-900">{{ subCategory.dateCreated }}</td>
                    <td class="px-6 py-4 text-center text-gray-900">{{ subCategory.lastUpdated }}</td>
                    <td class="w-px py-4 ps-6 pe-4 text-center">
                      <div class="relative inline-flex items-center justify-center">
                        <button
                          (click)="toggleDropdown(subCategory.id, $event)"
                          class="hover-bg-border flex h-[28px] w-[28px] items-center justify-center rounded-[6px] text-gray-600 hover:cursor-pointer hover:text-gray-900">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round">
                            <circle cx="12" cy="12" r="1"></circle>
                            <circle cx="12" cy="5" r="1"></circle>
                            <circle cx="12" cy="19" r="1"></circle>
                          </svg>
                        </button>
                        @if (activeDropdownId() == subCategory.id) {
                          <div
                            [class.top-full]="i < paginatedSubCategories.length - 2"
                            [class.mt-1]="i < paginatedSubCategories.length - 2"
                            [class.bottom-full]="i >= paginatedSubCategories.length - 2"
                            [class.mb-1]="i >= paginatedSubCategories.length - 2"
                            class="absolute right-0 z-50 flex w-48 flex-col gap-1 rounded-md border border-gray-200 bg-white p-1 shadow-lg">
                            <button
                              (click)="handleAction('Edit Sub Category', subCategory)"
                              class="w-full rounded-[4px] px-3 py-2 text-left text-[13px] text-gray-700 hover:cursor-pointer hover:bg-gray-50">
                              <span i18n="@@adminActionEditSubCategory">Edit Sub Category</span>
                            </button>
                            <button
                              (click)="handleAction('Delete Sub Category', subCategory)"
                              class="w-full rounded-[4px] px-3 py-2 text-left text-[13px] text-gray-700 hover:cursor-pointer hover:bg-gray-50">
                              <span i18n="@@adminActionDeleteSubCategory">Delete Sub Category</span>
                            </button>
                          </div>
                        }
                      </div>
                    </td>
                  </tr>
                }
              }
            </tbody>
          </table>
        </div>

        <div class="mt-2 flex items-center justify-between">
          <div class="text-[14px]">
            <span i18n="@@adminProductsPaginationShowing">Showing</span>
            <span class="text-primary mx-1">{{ startItem }}-{{ endItem }}</span>
            <span i18n="@@adminProductsPaginationOf">of</span>
            <span class="text-primary mx-1">{{ totalItems }}</span>
          </div>
          <div class="flex items-center gap-1">
            @if (isArabic()) {
              <button
                (click)="previousPage()"
                [disabled]="currentPage == totalPages"
                [class.opacity-50]="currentPage == totalPages"
                [class.cursor-not-allowed]="currentPage == totalPages"
                class="border-border hover-bg-surface-subtle flex h-[32px] w-[32px] items-center justify-center rounded-[6px] border hover:cursor-pointer disabled:hover:bg-transparent">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            } @else {
              <button
                (click)="previousPage()"
                [disabled]="currentPage == 1"
                [class.opacity-50]="currentPage == 1"
                [class.cursor-not-allowed]="currentPage == 1"
                class="border-border hover-bg-surface-subtle flex h-[32px] w-[32px] items-center justify-center rounded-[6px] border hover:cursor-pointer disabled:hover:bg-transparent">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
            }
            @if (totalPages <= 5) {
              @for (page of getPageNumbers(); track page) {
                @if (currentPage == page) {
                  <button
                    (click)="goToPage(page)"
                    class="border-border bg-primary flex h-[32px] w-[32px] items-center justify-center rounded-[6px] border text-[14px] font-[500] text-white hover:cursor-pointer">
                    {{ page }}
                  </button>
                } @else {
                  <button
                    (click)="goToPage(page)"
                    class="border-border hover-bg-surface-subtle flex h-[32px] w-[32px] items-center justify-center rounded-[6px] border text-[14px] font-[500] hover:cursor-pointer">
                    {{ page }}
                  </button>
                }
              }
            } @else {
              @if (currentPage == 1) {
                <button
                  (click)="goToPage(1)"
                  class="border-border bg-primary flex h-[32px] w-[32px] items-center justify-center rounded-[6px] border text-[14px] font-[500] text-white hover:cursor-pointer">
                  1
                </button>
              } @else {
                <button
                  (click)="goToPage(1)"
                  class="border-border hover-bg-surface-subtle flex h-[32px] w-[32px] items-center justify-center rounded-[6px] border text-[14px] font-[500] hover:cursor-pointer">
                  1
                </button>
              }
              @if (currentPage > 3) {
                <span class="px-1 text-[14px]">...</span>
              }
              @if (currentPage > 2 && currentPage < totalPages - 1) {
                <button
                  (click)="goToPage(currentPage - 1)"
                  class="border-border hover-bg-surface-subtle flex h-[32px] w-[32px] items-center justify-center rounded-[6px] border text-[14px] font-[500] hover:cursor-pointer">
                  {{ currentPage - 1 }}
                </button>
              }
              @if (currentPage != 1 && currentPage != totalPages) {
                <button
                  (click)="goToPage(currentPage)"
                  class="border-border bg-primary flex h-[32px] w-[32px] items-center justify-center rounded-[6px] border text-[14px] font-[500] text-white hover:cursor-pointer">
                  {{ currentPage }}
                </button>
              }
              @if (currentPage < totalPages - 2) {
                <button
                  (click)="goToPage(currentPage + 1)"
                  class="border-border hover-bg-surface-subtle flex h-[32px] w-[32px] items-center justify-center rounded-[6px] border text-[14px] font-[500] hover:cursor-pointer">
                  {{ currentPage + 1 }}
                </button>
              }
              @if (currentPage < totalPages - 2) {
                <span class="px-1 text-[14px]">...</span>
              }
              @if (currentPage == totalPages) {
                <button
                  (click)="goToPage(totalPages)"
                  class="border-border bg-primary flex h-[32px] w-[32px] items-center justify-center rounded-[6px] border text-[14px] font-[500] text-white hover:cursor-pointer">
                  {{ totalPages }}
                </button>
              } @else {
                <button
                  (click)="goToPage(totalPages)"
                  class="border-border hover-bg-surface-subtle flex h-[32px] w-[32px] items-center justify-center rounded-[6px] border text-[14px] font-[500] hover:cursor-pointer">
                  {{ totalPages }}
                </button>
              }
            }
            @if (isArabic()) {
              <button
                (click)="nextPage()"
                [disabled]="currentPage == 1"
                [class.opacity-50]="currentPage == 1"
                [class.cursor-not-allowed]="currentPage == 1"
                class="border-border hover-bg-surface-subtle flex h-[32px] w-[32px] items-center justify-center rounded-[6px] border hover:cursor-pointer disabled:hover:bg-transparent">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round">
                  <polyline points="15 18 9 12 15 6"></polyline>
                </svg>
              </button>
            } @else {
              <button
                (click)="nextPage()"
                [disabled]="currentPage == totalPages"
                [class.opacity-50]="currentPage == totalPages"
                [class.cursor-not-allowed]="currentPage == totalPages"
                class="border-border hover-bg-surface-subtle flex h-[32px] w-[32px] items-center justify-center rounded-[6px] border hover:cursor-pointer disabled:hover:bg-transparent">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round">
                  <polyline points="9 18 15 12 9 6"></polyline>
                </svg>
              </button>
            }
          </div>
        </div>
      }
    </div>
  </div>
}

@if (showSuccessMessage) {
  <div class="fixed top-6 right-6 z-50">
    <div class="bg-green flex items-center gap-3 rounded-[8px] px-6 py-4 shadow-lg">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="white"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round">
        <polyline points="20 6 9 17 4 12"></polyline>
      </svg>
      <p class="text-[14px] font-[500] text-white" i18n="@@adminCategoriesDeleteSuccess">
        Category deleted successfully!
      </p>
    </div>
  </div>
}

@if (showDeleteDialog) {
  <div
    class="fixed inset-0 z-50 flex items-center justify-center"
    style="background-color: rgba(175, 175, 175, 0.5)"
    (click)="closeDeleteDialog()">
    <div
      class="flex w-[400px] flex-col items-center gap-6 rounded-[12px] bg-white p-8 shadow-lg"
      (click)="$event.stopPropagation()">
      <div class="bg-danger-bg flex h-[80px] w-[80px] items-center justify-center rounded-full">
        <svg
          class="stroke-danger-text"
          xmlns="http://www.w3.org/2000/svg"
          width="48"
          height="48"
          viewBox="0 0 24 24"
          fill="none"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round">
          <polyline points="3 6 5 6 21 6"></polyline>
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          <line x1="10" y1="11" x2="10" y2="17"></line>
          <line x1="14" y1="11" x2="14" y2="17"></line>
        </svg>
      </div>

      <div class="flex flex-col items-center gap-2 text-center">
        <h2 class="text-black-soft text-[20px] font-[600]" i18n="@@adminDeleteCategoryDialogTitle">Delete Category?</h2>
        <p class="text-neutral-text text-[14px]" i18n="@@adminDeleteCategoryConfirmMessage">
          Are you sure you want to delete
          <span class="text-primary px-2 font-[600]">{{ isArabic() ? category?.titleAr : category?.titleEn }}</span>
          ?
        </p>
      </div>

      <div class="flex w-full gap-3">
        <button
          (click)="closeDeleteDialog()"
          class="border-border text-black-soft hover-bg-surface-subtle flex-1 rounded-[6px] border bg-white px-6 py-3 text-[14px] font-[500] hover:cursor-pointer">
          <span i18n="@@adminDeleteDialogCancel">Cancel</span>
        </button>
        <button
          (click)="deleteCategory()"
          class="bg-primary hover-bg-primary-hover flex-1 rounded-[6px] px-6 py-3 text-[14px] font-[500] text-white hover:cursor-pointer">
          <span i18n="@@adminDeleteDialogConfirm">Delete</span>
        </button>
      </div>
    </div>
  </div>
}
